<script setup lang="ts">
import { ref } from 'vue'
import BuildingBlockList from '@/components/building-block-form/BuildingBlockList.vue'
import AddBuildingBlockTypeDialog from '@/views/building_blocks/components/AddBuildingBlockTypeDialog.vue'
import type { CalculationFormMixinProps } from '@/components/calculation-form/type/type'
import { useBuildingBlockView } from '@/views/building_blocks/composable/useBuildingBlockView'
import type { BuildingBlockType } from '@/stores/buildingBlock'

const props = withDefaults(defineProps<CalculationFormMixinProps>(), {
  tab: 'BUILDING_BLOCKS',
  disabled: false,
  readonly: false
})
const {
  isReadOnly,
  loading,
  operationLoadingVisible,
  operationLoadingMessage,
  infoMenu,
  hasSelectedDatabase,
  selectedDatabaseName,
  addBuildingBlock,
  calculationId,
  hasCalculationContext,
  hasCustomBuildingBlocks,
  switchValidationByBlock,
  isBlockedByMissingIssues
} = useBuildingBlockView(props)

const showAddBlockTypeDialog = ref(false)

const onSelectBlockType = async (blockType: BuildingBlockType) => {
  await addBuildingBlock(blockType)
  showAddBlockTypeDialog.value = false
}
</script>

<template>
  <template v-if="loading">
    <div class="d-flex justify-center align-center fill-height pa-6">
      <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
    </div>
  </template>
  <template v-else>
    <div>
      <v-overlay
        :model-value="operationLoadingVisible"
        class="d-flex align-center justify-center"
        persistent
      >
        <v-card class="operation-loading-card text-center py-7 px-8">
          <div class="d-flex justify-center mb-4">
            <v-progress-circular
              indeterminate
              color="primary"
              size="56"
              width="5"
            ></v-progress-circular>
          </div>
          <div class="operation-loading-text">{{ operationLoadingMessage }}</div>
        </v-card>
      </v-overlay>

      <v-container fluid class="pa-6">
        <!-- Top bar -->
        <v-row class="mb-4">
          <v-col cols="12" md="auto">
            <h1 class="text-h4">Building blocks</h1>
          </v-col>
          <v-spacer />
          <v-col cols="12" md="auto" class="d-flex flex-wrap ga-2 align-center">
            <v-menu v-model="infoMenu" location="bottom">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  size="medium"
                  border="md primary opacity-75"
                  variant="outlined"
                  class="mx-2"
                >
                  <v-icon>mdi-information-symbol</v-icon>
                </v-btn>
              </template>

              <v-card style="max-width: 520px; padding: 12px">
                <div
                  v-if="hasCustomBuildingBlocks"
                  style="font-size: 0.95rem; color: var(--v-theme-on-surface-variant)"
                >
                  Switching environmental database will be validated first against custom building
                  blocks. Missing and changed rows will be highlighted in the affected custom blocks
                  after switching.
                </div>
                <div v-else style="font-size: 0.95rem; color: var(--v-theme-on-surface-variant)">
                  Environmental database selection is managed in the Environmental Database tab.
                  Import and selection are managed there.
                </div>
              </v-card>
            </v-menu>
          </v-col>
        </v-row>

        <div class="mb-4">
          <p>
            Building blocks represent environmental profiles used in sustainability calculations.
            Default building blocks are sourced from the selected environmental database. For Dutch
            projects, the standard database is the <i>Nationale Milieu Database (NMD)</i>.
          </p>
          <p class="mt-2">
            Manage database import and selection in the <b>Environmental Database</b> tab. Once your
            preferred database is selected, you may create customized building blocks by selecting
            <b>Add a New Building Block</b>. These customized profiles will appear in your
            calculations alongside the default options.
          </p>
          <p class="mt-2 text-body-2 text-medium-emphasis">
            <b>Note:</b> The <b>Production</b> column is calculated as A1 + A2 + A3 when any
            individual phase value is provided. If A1, A2, and A3 are all zero (as is typical for
            NMD data), the combined A1-A3 value is used instead. Activities for A1-A3 (combined) and
            individual A1/A2/A3 are mutually exclusive — selecting one will disable the other.
          </p>
          <v-alert
            v-if="!hasCalculationContext"
            class="mt-3"
            type="info"
            variant="tonal"
            density="compact"
          >
            Select a calculation first to manage building blocks.
          </v-alert>

          <v-alert
            v-if="hasCalculationContext && hasSelectedDatabase"
            class="mt-3"
            type="info"
            variant="tonal"
            density="compact"
          >
            Current environmental database: <b>{{ selectedDatabaseName }}</b
            >. Default building blocks and product searches in this tab use this database.
          </v-alert>

          <v-alert
            v-if="hasCalculationContext && !hasSelectedDatabase"
            class="mt-3"
            type="warning"
            variant="tonal"
            density="compact"
          >
            No environmental database selected for this calculation yet. Select one in the
            Environmental Database tab before adding or searching building blocks.
          </v-alert>

          <v-btn
            color="primary"
            prepend-icon="mdi-plus"
            class="mt-3"
            @click="showAddBlockTypeDialog = true"
            :disabled="
              !hasSelectedDatabase || !calculationId || isReadOnly || isBlockedByMissingIssues
            "
          >
            Add a new building block
          </v-btn>
        </div>

        <!-- Custom building block label -->
        <v-chip label size="small" class="mb-4">
          <v-icon start>mdi-copyright</v-icon>
          Custom building block
        </v-chip>

        <!-- Building block cards -->
        <BuildingBlockList
          :calculation-id="calculationId"
          :readonly="isReadOnly"
          :switch-validation-by-block="switchValidationByBlock"
        />
      </v-container>

      <AddBuildingBlockTypeDialog v-model="showAddBlockTypeDialog" @select="onSelectBlockType" />
    </div>
  </template>
</template>

<style scoped>
.operation-loading-card {
  width: 420px;
  max-width: calc(100vw - 48px);
}

.operation-loading-text {
  font-size: 0.95rem;
  font-weight: 400;
  line-height: 1.45;
}
</style>
